import { useState, useEffect } from "react";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import Button from "./button.core";
import type { IAssetMinimal } from "../../../types/models/asset.models.types";
import { ThreeDWrapper } from "./3d-wrapper.core";
import EditIcon from "../../assets/svgs/edit.svg";
import DeleteIcon from "../../../assets/svgs/common/delete.svg";
import UploadIcon from "../../../assets/svgs/common/upload.svg";
import DocumentSmall from "../../../assets/svgs/campaigns/doc.svg";

type AssetUploadProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  trackingName?: string;
  label: string;
  uploadType: "image" | "video" | "both" | "all";
  required?: boolean;
  placeholder?: string;
  description?: string;
  maxFiles: number;
  multiple?: boolean;
  maxSize?: number;
  buttonLabel?: string;
  onUploadClick?: () => void;
  onEdit?: (index: number) => void;
};

const AssetUpload = <T extends FieldValues>({
  form,
  name,
  trackingName,
  label,
  placeholder = "Upload files",
  description = "File should not be more then 20 mb",
  buttonLabel = "Upload more",
  onUploadClick,
  onEdit,
}: AssetUploadProps<T>) => {
  const [assets, setAssets] = useState<IAssetMinimal[]>([]);

  // Watch the trackingName field for changes
  const trackedAssets = trackingName ? form.watch(trackingName as Path<T>) : [];

  // Update local state when tracked assets change
  useEffect(() => {
    if (trackingName && Array.isArray(trackedAssets)) {
      setAssets(trackedAssets as IAssetMinimal[]);
    }
  }, [trackingName, trackedAssets]);

  const assetsLength = assets?.length || 0;
  const fieldValue = form.watch(name) as
    | Array<{ file: File; note?: string }>
    | undefined;
  const files = fieldValue || [];

  const handleClearUpload = (assetId?: string, index?: number) => {
    if (assetId && trackingName) {
      // Remove from tracked assets
      const updatedAssets = assets.filter((asset) => asset.id !== assetId);

      // Update form value
      form.setValue(trackingName as Path<T>, updatedAssets as T[Path<T>], {
        shouldDirty: true,
        shouldValidate: true,
      });

      // Update local state immediately
      setAssets(updatedAssets);
    }

    if (index !== undefined) {
      const currentFiles = form.getValues(name) as Array<{
        file: File;
        note?: string;
      }>;
      const updatedFiles = currentFiles.filter((_, i) => i !== index);
      form.setValue(name, updatedFiles as T[Path<T>], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const error = form.formState.errors[name]?.message as string | undefined;
  const hasFiles = files.length > 0 || assetsLength > 0;

  return (
    <div className="flex flex-col gap-4 p-6 border-2 border-gray-300 border-dashed rounded-xl w-full min-h-[500px] max-h-[500px] overflow-y-auto">
      <p className="flex flex-col font-medium text-base">
        {label}
       <span className="text-secondary text-xs"> (Supported formats: .jpeg, .jpg, .png, .mp4, .mov)</span>
      </p>

      {/* Files List */}
      {hasFiles && (
        <div className="flex flex-col gap-4">
          {/* Uploaded Files */}
          {files.map((item, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            <div key={index} className="flex flex-col gap-3">
              <div className="flex justify-between items-center gap-4 px-4 py-3">
                <div className="flex items-center gap-3">
                  <ThreeDWrapper
                    hover={false}
                    className="flex justify-center items-center p-0 rounded-lg"
                  >
                    <DocumentSmall />
                  </ThreeDWrapper>
                  <p
                    className="max-w-[200px] font-medium text-sm truncate"
                    title={item.file.name}
                  >
                    {item.file.name.length > 30
                      ? `${item.file.name.substring(0, 30)}...`
                      : item.file.name}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onEdit?.(index)}
                    className="rounded-lg transition-colors"
                  >
                    <EditIcon />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleClearUpload(undefined, index)}
                    className="rounded-lg text-red-500 transition-colors"
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>

              {/* Notes TextArea */}
              {item.note && (
                <p className="bg-gray-50 p-5 rounded-10 text-wrap">
                  {item.note}
                </p>
              )}
            </div>
          ))}

          {/* Existing Assets */}
          {assets.map((asset) => (
            <div key={asset.id} className="flex flex-col gap-3">
              <div className="flex justify-between items-center gap-4 px-4 py-3 rounded-xl">
                <div className="flex items-center gap-3">
                  <ThreeDWrapper
                    hover={false}
                    className="flex justify-center items-center p-0 rounded-lg"
                  >
                    <DocumentSmall />
                  </ThreeDWrapper>

                  <p className="font-medium text-sm">{asset.name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleClearUpload(asset.id)}
                    className="hover:bg-red-50 rounded-lg text-red-500 transition-colors"
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>
              {asset.note && (
                <p className="bg-gray-50 p-5 rounded-10 text-wrap">
                  {asset.note}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {!hasFiles && (
        <div className="flex flex-col flex-1 justify-center items-center gap-2">
          <UploadIcon />
          <p className="font-medium text-base">{placeholder}</p>
          <p className="text-gray-400 text-sm">{description}</p>
        </div>
      )}

      {/* Upload Button */}
      <div className={hasFiles ? "" : "mt-auto"}>
        <Button
          type="button"
          onClick={onUploadClick}
          variant="secondary"
          className="px-6 py-3 border-2 border-black rounded-xl w-full font-semibold text-base"
        >
          {hasFiles ? buttonLabel : placeholder}
        </Button>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  );
};

export default AssetUpload;
